import { createColumnHelper } from '@tanstack/react-table';
import React from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Switch,
  Table,
  Textarea,
  unwrapTrpcData,
} from '@org/ui';
import { trpc, RouterOutputs } from '@org/clients/school';
import { CONVERSATION_LEVEL_BANDS, type ConversationLevelBand } from '@org/model';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getMutationErrorMessage } from '../shared/multi-step-utils';

type TopicRow = RouterOutputs['conversationPractice']['topics']['list'][number];

type FormState = {
  slug: string;
  titlePt: string;
  titleEn: string;
  descriptionPt: string;
  descriptionEn: string;
  minLevel: ConversationLevelBand;
  maxLevel: ConversationLevelBand;
  targetStructures: string;
  guidance: string;
  order: number;
  isActive: boolean;
};

const EMPTY_FORM: FormState = {
  slug: '',
  titlePt: '',
  titleEn: '',
  descriptionPt: '',
  descriptionEn: '',
  minLevel: 'A1',
  maxLevel: 'A1',
  targetStructures: '',
  guidance: '',
  order: 0,
  isActive: true,
};

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const slugify = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const bandIndex = (band: string) => (CONVERSATION_LEVEL_BANDS as readonly string[]).indexOf(band);

export const ConversationTopicsList: React.FC = () => {
  const { t } = useTranslation('admin');
  const utils = trpc.useUtils();
  const { data } = trpc.conversationPractice.topics.list.useQuery();
  const [filterValue, setFilterValue] = React.useState('');
  const [pendingDelete, setPendingDelete] = React.useState<TopicRow | null>(null);
  const [editing, setEditing] = React.useState<{ id: string | null; form: FormState } | null>(null);
  const [formError, setFormError] = React.useState<string | null>(null);

  const rows = React.useMemo(() => {
    const list = unwrapTrpcData<TopicRow[]>(data as TopicRow[]) ?? [];
    return [...list].sort((a, b) => a.order - b.order || a.titlePt.localeCompare(b.titlePt));
  }, [data]);

  const invalidate = async () => {
    await utils.conversationPractice.topics.list.invalidate();
    await utils.conversationPractice.getTopics.invalidate();
  };

  const createMutation = trpc.conversationPractice.topics.create.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success(t('conversationTopics.toasts.saveSuccess'));
      setEditing(null);
    },
    onError: (error: unknown) =>
      toast.error(t('conversationTopics.toasts.saveError', { message: getMutationErrorMessage(error) })),
  });
  const updateMutation = trpc.conversationPractice.topics.update.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success(t('conversationTopics.toasts.saveSuccess'));
      setEditing(null);
    },
    onError: (error: unknown) =>
      toast.error(t('conversationTopics.toasts.saveError', { message: getMutationErrorMessage(error) })),
  });
  const deleteMutation = trpc.conversationPractice.topics.delete.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success(t('conversationTopics.toasts.removeSuccess'));
    },
    onError: (error: unknown) =>
      toast.error(t('conversationTopics.toasts.removeError', { message: getMutationErrorMessage(error) })),
  });

  const openNew = () => {
    const nextOrder = rows.length ? Math.max(...rows.map((row) => row.order)) + 10 : 10;
    setFormError(null);
    setEditing({ id: null, form: { ...EMPTY_FORM, order: nextOrder } });
  };

  const openEdit = (row: TopicRow) => {
    setFormError(null);
    setEditing({
      id: row.conversationTopicId,
      form: {
        slug: row.slug,
        titlePt: row.titlePt,
        titleEn: row.titleEn,
        descriptionPt: row.descriptionPt,
        descriptionEn: row.descriptionEn,
        minLevel: row.minLevel,
        maxLevel: row.maxLevel,
        targetStructures: row.targetStructures ?? '',
        guidance: row.guidance ?? '',
        order: row.order,
        isActive: row.isActive,
      },
    });
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setEditing((current) => (current ? { ...current, form: { ...current.form, [key]: value } } : current));
  };

  const submit = () => {
    if (!editing) return;
    const { form } = editing;
    const slug = editing.id ? form.slug : form.slug || slugify(form.titleEn || form.titlePt);
    if (!form.titlePt.trim() || !form.titleEn.trim() || !form.descriptionPt.trim() || !form.descriptionEn.trim()) {
      setFormError(t('conversationTopics.validation.required'));
      return;
    }
    if (!SLUG_RE.test(slug)) {
      setFormError(t('conversationTopics.validation.slug'));
      return;
    }
    if (bandIndex(form.maxLevel) < bandIndex(form.minLevel)) {
      setFormError(t('conversationTopics.validation.levels'));
      return;
    }
    setFormError(null);
    const payload = {
      slug,
      titlePt: form.titlePt.trim(),
      titleEn: form.titleEn.trim(),
      descriptionPt: form.descriptionPt.trim(),
      descriptionEn: form.descriptionEn.trim(),
      minLevel: form.minLevel,
      maxLevel: form.maxLevel,
      targetStructures: form.targetStructures.trim() || null,
      guidance: form.guidance.trim() || null,
      order: Number.isFinite(form.order) ? form.order : 0,
      isActive: form.isActive,
    };
    if (editing.id) {
      updateMutation.mutate({ id: editing.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate({ id: pendingDelete.conversationTopicId });
    setPendingDelete(null);
  };

  const columnHelper = createColumnHelper<TopicRow>();
  const columns = [
    columnHelper.accessor((row) => row.order, {
      id: 'order',
      header: t('conversationTopics.columns.order'),
      cell: (info) => <span className="text-muted-foreground">{info.getValue()}</span>,
    }),
    columnHelper.accessor((row) => `${row.titlePt} ${row.titleEn}`, {
      id: 'title',
      header: t('conversationTopics.columns.title'),
      cell: (info) => (
        <div>
          <div className="font-medium">{info.row.original.titlePt}</div>
          <div className="text-xs text-muted-foreground">{info.row.original.titleEn}</div>
        </div>
      ),
    }),
    columnHelper.accessor((row) => `${row.minLevel}-${row.maxLevel}`, {
      id: 'levels',
      header: t('conversationTopics.columns.levels'),
      cell: (info) => {
        const row = info.row.original;
        return <Badge variant="outline">{row.minLevel === row.maxLevel ? row.minLevel : `${row.minLevel} a ${row.maxLevel}`}</Badge>;
      },
    }),
    columnHelper.accessor((row) => row.targetStructures ?? '', {
      id: 'structures',
      header: t('conversationTopics.columns.structures'),
      cell: (info) => <span className="text-sm text-muted-foreground">{info.getValue()}</span>,
    }),
    columnHelper.accessor((row) => (row.isActive ? 'active' : 'inactive'), {
      id: 'status',
      header: t('conversationTopics.columns.status'),
      cell: (info) => (
        <Badge variant={info.row.original.isActive ? 'outline' : 'secondary'}>
          {info.row.original.isActive ? t('conversationTopics.status.active') : t('conversationTopics.status.inactive')}
        </Badge>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('conversationTopics.columns.actions'),
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => openEdit(row)}>
              {t('conversationTopics.actions.edit')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-red-600"
              disabled={deleteMutation.isPending}
              onClick={() => setPendingDelete(row)}
            >
              {t('conversationTopics.actions.remove')}
            </Button>
          </div>
        );
      },
    }),
  ];

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('conversationTopics.title')}</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">{t('conversationTopics.description')}</p>
        </div>
        <Button onClick={openNew}>{t('conversationTopics.new')}</Button>
      </div>
      <input
        type="search"
        value={filterValue}
        onChange={(event) => setFilterValue(event.target.value)}
        placeholder={t('conversationTopics.searchPlaceholder')}
        className="w-full max-w-sm rounded-md border border-border bg-background px-3 py-2 text-sm"
      />
      <Table
        tableData={rows as any}
        columns={columns}
        getRowId={(row: any) => row.conversationTopicId}
        globalFilter={filterValue}
      />

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? t('conversationTopics.edit') : t('conversationTopics.new')}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ct-title-pt">{t('conversationTopics.fields.titlePt')}</Label>
                <Input id="ct-title-pt" value={editing.form.titlePt} onChange={(e) => setField('titlePt', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ct-title-en">{t('conversationTopics.fields.titleEn')}</Label>
                <Input id="ct-title-en" value={editing.form.titleEn} onChange={(e) => setField('titleEn', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ct-desc-pt">{t('conversationTopics.fields.descriptionPt')}</Label>
                <Textarea id="ct-desc-pt" rows={3} value={editing.form.descriptionPt} onChange={(e) => setField('descriptionPt', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ct-desc-en">{t('conversationTopics.fields.descriptionEn')}</Label>
                <Textarea id="ct-desc-en" rows={3} value={editing.form.descriptionEn} onChange={(e) => setField('descriptionEn', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ct-min">{t('conversationTopics.fields.minLevel')}</Label>
                <select
                  id="ct-min"
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={editing.form.minLevel}
                  onChange={(e) => setField('minLevel', e.target.value as ConversationLevelBand)}
                >
                  {CONVERSATION_LEVEL_BANDS.map((band) => (
                    <option key={band} value={band}>{band}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ct-max">{t('conversationTopics.fields.maxLevel')}</Label>
                <select
                  id="ct-max"
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={editing.form.maxLevel}
                  onChange={(e) => setField('maxLevel', e.target.value as ConversationLevelBand)}
                >
                  {CONVERSATION_LEVEL_BANDS.map((band) => (
                    <option key={band} value={band}>{band}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="ct-structures">{t('conversationTopics.fields.targetStructures')}</Label>
                <Input id="ct-structures" value={editing.form.targetStructures} onChange={(e) => setField('targetStructures', e.target.value)} />
                <p className="text-xs text-muted-foreground">{t('conversationTopics.fields.targetStructuresHint')}</p>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="ct-guidance">{t('conversationTopics.fields.guidance')}</Label>
                <Textarea id="ct-guidance" rows={4} value={editing.form.guidance} onChange={(e) => setField('guidance', e.target.value)} />
                <p className="text-xs text-muted-foreground">{t('conversationTopics.fields.guidanceHint')}</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ct-order">{t('conversationTopics.fields.order')}</Label>
                <Input
                  id="ct-order"
                  type="number"
                  value={editing.form.order}
                  onChange={(e) => setField('order', Number.parseInt(e.target.value, 10) || 0)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ct-slug">{t('conversationTopics.fields.slug')}</Label>
                <Input
                  id="ct-slug"
                  value={editing.form.slug}
                  disabled={editing.id !== null}
                  placeholder={slugify(editing.form.titleEn || editing.form.titlePt)}
                  onChange={(e) => setField('slug', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">{t('conversationTopics.fields.slugHint')}</p>
              </div>
              <div className="flex items-center gap-3 sm:col-span-2">
                <Switch id="ct-active" checked={editing.form.isActive} onCheckedChange={(checked) => setField('isActive', checked)} />
                <Label htmlFor="ct-active">{t('conversationTopics.fields.isActive')}</Label>
              </div>
              {formError && <p className="text-sm text-red-600 sm:col-span-2">{formError}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
              {t('conversationTopics.actions.cancel')}
            </Button>
            <Button onClick={submit} disabled={saving}>
              {t('conversationTopics.actions.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        description={t('conversationTopics.removeConfirm', { title: pendingDelete?.titlePt ?? '' })}
        confirmLabel={t('conversationTopics.actions.remove')}
        destructive
        onConfirm={confirmDelete}
      />
    </div>
  );
};
